import { HttpContext, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';

import {
  AUTH_CONTEXT,
  AuthContextsForEndPoints,
  AuthInterceptor,
  AuthInterceptorConfig,
  AuthService,
  SERVICE_TOKEN_INTERCEPTOR_CONFIG,
  ServiceAccessTokenProvider,
  ServiceTokenInterceptorConfig,
  TenantService
} from '@celum/authentication';
import { CelumPropertiesProvider, hasValue } from '@celum/core';
import { TranslationHelper } from '@celum/ng2base/translation';
import { PortalConfiguration, PortalsProperties } from '@celum/portals/domain';
import {
  ACCESS_REQUEST_PATH,
  ConfigurationResolver,
  getAuthContextForGuestToken,
  getRouteParamRecursiveForRoot,
  isCustomTokenUrl,
  isIdentityAndCloudTokenUrl,
  PORTAL_CLOUD_TOKEN_CLIENT_ID,
  PORTAL_CLOUD_TOKEN_ENDPOINT,
  PORTAL_GUEST_TOKEN_ENDPOINT
} from '@celum/portals/shared';

@Injectable()
export class PortalsAuthInterceptor extends AuthInterceptor {
  private readonly portalConfigUrl = `${PortalsProperties.properties.apiUrl}/configuration/`;
  private readonly saccUserUrl = `${CelumPropertiesProvider.properties.authentication.saccUrl}/users/current`;

  private isInteractiveLoginAlreadyRequired = false;

  constructor(
    authService: AuthService,
    protected override router: Router,
    @Inject(DOCUMENT) document: Document,
    private tokenProvider: ServiceAccessTokenProvider,
    translation: TranslationHelper,
    @Inject(SERVICE_TOKEN_INTERCEPTOR_CONFIG) private interceptorConf: ServiceTokenInterceptorConfig<AuthInterceptorConfig>,
    private tenantService: TenantService,
    private configuration: ConfigurationResolver
  ) {
    super(authService, router, document, tokenProvider, translation, interceptorConf);
  }

  public override intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Initial config request? Used to determine whether all calls need to be authenticated
    if (req.url.startsWith(this.portalConfigUrl)) {
      this.tokenProvider.clearTokenCache(PORTAL_CLOUD_TOKEN_CLIENT_ID);
      // already stored? Check whether we need authentication
      const tenantId = this.tenantService.getCurrentTenantId();
      if (tenantId) {
        return super.intercept(req, next).pipe(catchError(error => this.handleUnauthorizedError(error)));
      }
      // Not stored? Make the request normally. If it fails with a 403 we will store the tenantId/portalId and return after login, ready to add the token
      return next.handle(req);
    }

    if (req.url.startsWith(CelumPropertiesProvider.properties.font?.apiUrl)) {
      return next.handle(req.clone({ setHeaders: { 'X-Tenant': this.tenantService.getCurrentTenantId() } }));
    }

    req = this.attachAuthContextIfNecessary(req);

    // All other calls should only be authenticated if the portal is protected, the user is already authenticated or requires a cloud or identity token
    // At this point portal configuration loading was already attempted (cmp. `PortalsAuthGuard` and `ConfigurationResolver`) and in case it's falsy, the
    // config could not be loaded (due to e.g. 403, etc.)
    return this.configuration.configuration$.pipe(
      take(1),
      switchMap(config => this.refreshExpiredB2CTokenSilently().pipe(map(() => config))),
      switchMap(config => {
        if (this.shouldTokensBeAttached(config, req)) {
          return super.intercept(req, next).pipe(catchError(error => this.handleUnauthorizedError(error)));
        }
        return next.handle(req);
      })
    );
  }

  /**
   * Determines whether tokens should be attached to the request.
   *
   * Tokens should be attached if:
   * - The portal configuration is protected
   * - A valid B2C token is already available (that's the 'authenticated access of public portals' usecase)
   * - The request URL is a custom token URL
   *
   * @param config the portal configuration
   * @param req the HTTP request
   */
  private shouldTokensBeAttached(config: PortalConfiguration, req: HttpRequest<any>): boolean {
    const isSaccUserFetch = req.url.startsWith(this.saccUserUrl);
    const isAccessRequest = req.url.startsWith(PortalsProperties.properties.apiUrl) && req.url.includes(ACCESS_REQUEST_PATH);

    // no further checks for user fetches (we always want to "allow" that (also for public portals) and attach a token)
    // and for access requests (they happen on a fallback page if access to the portal is not yet provided)
    if (isSaccUserFetch || isAccessRequest) {
      return true;
    }

    if (!config) {
      return false;
    }

    return config.protected || this.authService.isB2CTokenStillValid() || isCustomTokenUrl(this.interceptorConf, req.url);
  }

  private attachAuthContextIfNecessary(req: HttpRequest<any>): HttpRequest<any> {
    if (!isCustomTokenUrl(this.interceptorConf, req.url)) {
      return req;
    }

    const authContexts: AuthContextsForEndPoints = {};

    const portalId = this.getPortalIdOrSlug();

    if (portalId) {
      authContexts[PORTAL_CLOUD_TOKEN_ENDPOINT] = { portalId };
    }

    if (isIdentityAndCloudTokenUrl(this.interceptorConf, req.url)) {
      authContexts[PORTAL_GUEST_TOKEN_ENDPOINT] = getAuthContextForGuestToken();
    }

    if (hasValue(authContexts)) {
      const context = new HttpContext().set(AUTH_CONTEXT, authContexts);

      return req.clone({ context });
    }

    return req;
  }

  private getPortalIdOrSlug(): string {
    return getRouteParamRecursiveForRoot('portalSlug', this.router);
  }

  private handleUnauthorizedError(error: any): Observable<never> {
    // Catch and handle the 403 specifically - in that case the user has no access to the portal or organization, so we redirect to "not-found"
    if (error.status === 403 && !error.error?.orgId) {
      this.router.navigateByUrl('/not-found', { skipLocationChange: true });
      return EMPTY;
    }
    return throwError(() => error);
  }

  /**
   * Refreshes an expired B2C token without requiring user interaction.
   *
   * If an interactive login is necessary, silent token acquisition is not tried again to avoid unnecessary
   * roundtrips to backend.
   */
  private refreshExpiredB2CTokenSilently(): Observable<void> {
    if (this.authService.isB2CTokenStillValid()) {
      // cover the case where the B2C token was refreshed in another tab
      this.isInteractiveLoginAlreadyRequired = false;
      return of(void 0);
    }

    // let's exit early if an interactive login was already necessary once -> this is an optimization to avoid unnecessary roundtrips to backend
    // (checking whether a refresh token is expired requires BE calls)
    if (this.isInteractiveLoginAlreadyRequired) {
      return of(void 0);
    }

    // here we try to refresh the B2C token silently and remember if an interactive login would be necessary instead
    return this.authService.isInteractiveLoginRequired().pipe(
      tap(isInteractiveLoginRequiredNow => (this.isInteractiveLoginAlreadyRequired = isInteractiveLoginRequiredNow)),
      switchMap(isInteractiveLoginRequiredNow => (isInteractiveLoginRequiredNow ? of(void 0) : this.authService.getB2CToken(true))),
      map(() => {
        /* empty */
      })
    );
  }
}
